import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { backend_url } from "../App";
import "./CSS/AuditLog.css";

const actionColor = (action) => {
  if (action.toLowerCase().includes("delete")) return "danger";
  if (action.toLowerCase().includes("created")) return "success";
  return "info";
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${backend_url}/api/audit-logs`);
      if (res.data.success) setLogs(res.data.logs);
    } catch {
      // silently fail — audit log is a secondary view
    } finally {
      setLoading(false);
    }
  };

  const actions = useMemo(
    () => [...new Set(logs.map((l) => l.action))].sort(),
    [logs]
  );

  const filtered = logs.filter((l) => {
    const matchAction = actionFilter === "all" || l.action === actionFilter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      l.adminName?.toLowerCase().includes(q) ||
      l.action?.toLowerCase().includes(q) ||
      l.details?.toLowerCase().includes(q) ||
      l.targetType?.toLowerCase().includes(q);
    return matchAction && matchSearch;
  });

  return (
    <div className="al-page">
      <div className="al-banner">
        <div className="al-title-row">
          <h1 className="al-title">Audit Log</h1>
          <span className="al-badge">{logs.length} events</span>
        </div>
        <p className="al-subtitle">Track admin actions — who changed what, and when</p>
      </div>

      <div className="al-content">
        <div className="al-controls">
          <div className="al-search-wrap">
            <span>🔍</span>
            <input
              type="text"
              placeholder="Search by admin, action, or detail..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="all">All Actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <button className="al-refresh-btn" onClick={fetchLogs}>⟳ Refresh</button>
        </div>

        <div className="al-table-wrap">
          {loading ? (
            <div className="al-empty">Loading audit log...</div>
          ) : filtered.length === 0 ? (
            <div className="al-empty">No matching events</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Admin</th>
                  <th>Role</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => (
                  <tr key={log._id}>
                    <td className="al-time-cell">
                      {new Date(log.createdAt).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="al-admin-cell">{log.adminName || "System"}</td>
                    <td>
                      {log.adminRole && (
                        <span className="al-role-chip">{log.adminRole}</span>
                      )}
                    </td>
                    <td>
                      <span className={`al-action-badge ${actionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="al-target-cell">{log.targetType}</td>
                    <td className="al-details-cell">{log.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
